'use client';

import React, { useEffect, useRef } from 'react';
import {
  Box,
  Button,
  Chip,
  FormControl,
  FormGroup,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
  Checkbox,
  FormControlLabel,
} from '@mui/material';
import { EditIcon, RemoveIcon } from '@/shared/ui/display/EmojiIcon';
import { SERVICE_SET_LABELS } from '@/entities/visit-counsel/model/room-observing.types';
import type {
  AttrDef,
  AttributeCondition,
  ConditionEffect,
  ConditionRule,
  EffectType,
} from '../model/moving-item.types';
import { SentenceRuleEditor } from './SentenceRuleEditor';
import { effectToText, ruleToText } from '../lib/conditionToText';
import {
  SERVICE_TYPE_LABELS,
  SERVICE_TYPE_VALUES,
  blockedToAllowedServiceTypeKeys,
  resolveBlockedServiceTypeKeys,
  serviceTypeKeysToServiceSets,
  type ServiceTypeKey,
} from '@miso/counsel-schema';

const EFFECT_TYPE_LABELS: Record<EffectType, string> = {
  GUIDE_MESSAGE: '가이드 메시지',
  OPTION_HINT: '옵션 힌트',
  SERVICE_TYPE_CONSTRAINT: '서비스 제한',
};

export interface ConditionRowProps {
  condition: AttributeCondition;
  ownerAttrDef: AttrDef;
  ownerAttrIndex: number;
  attrValues?: string[];
  allAttrDefs: AttrDef[];
  onUpdateRule: (updates: Partial<ConditionRule>) => void;
  onUpdateEffect: (updates: Partial<ConditionEffect>) => void;
  onRemove: () => void;
  /** 목록 전체에서 동시에 펼쳐지는 편집 패널은 하나만 */
  rowKey: string;
  exclusiveEditRowKey: string | null;
  setExclusiveEditRowKey: (key: string | null) => void;
  onMoveEvalAttr?: (toAttrIndex: number) => void;
}

export function ConditionRow({
  condition,
  ownerAttrDef,
  ownerAttrIndex,
  attrValues = [],
  allAttrDefs,
  onUpdateRule,
  onUpdateEffect,
  onRemove,
  rowKey,
  exclusiveEditRowKey,
  setExclusiveEditRowKey,
  onMoveEvalAttr,
}: ConditionRowProps) {
  const { rule, effect } = condition;
  const snapshotRef = useRef<{ rule: ConditionRule; effect: ConditionEffect } | null>(null);
  const prevActiveRef = useRef(false);

  const editing = exclusiveEditRowKey === rowKey;

  const ownerAttrLabel = ownerAttrDef.name?.trim() || ownerAttrDef.key || '(키 없음)';

  /** 다른 행이 열리면 스냅샷으로 되돌림 */
  useEffect(() => {
    if (!editing && snapshotRef.current) {
      const snap = snapshotRef.current;
      onUpdateRule(snap.rule);
      onUpdateEffect(snap.effect);
      snapshotRef.current = null;
    }
  }, [editing, onUpdateRule, onUpdateEffect]);

  /** 편집 진입 시 스냅샷 (규칙 추가로 자동 열릴 때 포함). 종료 시 prevActive 리셋으로 재진입 가능 */
  useEffect(() => {
    if (editing && !prevActiveRef.current) {
      snapshotRef.current = {
        rule: JSON.parse(JSON.stringify(rule)) as ConditionRule,
        effect: JSON.parse(JSON.stringify(effect)) as ConditionEffect,
      };
    }
    prevActiveRef.current = editing;
  }, [editing, rule, effect]);

  const handleStartEdit = () => {
    snapshotRef.current = {
      rule: JSON.parse(JSON.stringify(rule)) as ConditionRule,
      effect: JSON.parse(JSON.stringify(effect)) as ConditionEffect,
    };
    setExclusiveEditRowKey(rowKey);
  };

  const handleCancel = () => {
    const snap = snapshotRef.current;
    if (snap) {
      onUpdateRule(snap.rule);
      onUpdateEffect(snap.effect);
    }
    snapshotRef.current = null;
    setExclusiveEditRowKey(null);
  };

  const handleDone = () => {
    snapshotRef.current = null;
    setExclusiveEditRowKey(null);
  };

  /** 체크 = 불가. 저장 표현은 허용 목록이라 여집합으로 뒤집어 보여준다 */
  const blockedTypeKeys = resolveBlockedServiceTypeKeys(effect);

  /** 이 규칙이 발동했을 때 상담에서 고를 수 있는 서비스 범위(조합) — 서버 판정과 동일 규칙 */
  const enabledServiceSets = serviceTypeKeysToServiceSets(
    blockedToAllowedServiceTypeKeys(blockedTypeKeys)
  );

  const handleServiceTypeToggle = (key: ServiceTypeKey) => {
    const nextBlocked = blockedTypeKeys.includes(key)
      ? blockedTypeKeys.filter((k) => k !== key)
      : [...blockedTypeKeys, key];
    onUpdateEffect({
      allowedServiceTypeKeys: blockedToAllowedServiceTypeKeys(nextBlocked),
      allowedServiceSets: undefined,
    });
  };

  const ruleChip = ruleToText(rule, ownerAttrDef, allAttrDefs);
  const effectChip = effectToText(effect);

  if (!editing) {
    return (
      <Box
        sx={{
          p: 1,
          border: 1,
          borderColor: 'divider',
          borderRadius: 1,
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          flexWrap: 'wrap',
        }}
      >
        <Chip label={`IF: ${ruleChip}`} size="small" variant="outlined" sx={{ maxWidth: '100%' }} />
        <Typography variant="body2" color="text.secondary">
          →
        </Typography>
        <Chip
          label={`THEN: ${effectChip}`}
          size="small"
          color="primary"
          variant="outlined"
          sx={{ maxWidth: '100%' }}
        />
        <Box sx={{ flexGrow: 1 }} />
        <IconButton size="small" onClick={handleStartEdit} aria-label="규칙 편집">
          <EditIcon fontSize="small" />
        </IconButton>
        <IconButton size="small" onClick={onRemove} aria-label="조건 삭제">
          <RemoveIcon fontSize="small" />
        </IconButton>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        p: 1,
        border: 1,
        borderColor: 'primary.light',
        borderRadius: 1,
        display: 'flex',
        flexDirection: 'column',
        gap: 1,
      }}
    >
      <SentenceRuleEditor
        rule={rule}
        attrType={ownerAttrDef.type}
        attrValues={attrValues}
        allAttrDefs={allAttrDefs}
        ownerAttrIndex={ownerAttrIndex}
        ownerAttrLabel={ownerAttrLabel}
        onChange={(next) => onUpdateRule(next)}
        onMoveEvalAttr={onMoveEvalAttr}
      />

      <Typography variant="subtitle2" color="primary">
        THEN (효과)
      </Typography>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, pl: 0.5 }}>
        <FormControl variant="filled" size="small" sx={{ minWidth: 140 }}>
          <InputLabel>효과 유형</InputLabel>
          <Select
            label="효과 유형"
            value={effect.type}
            onChange={(e) =>
              onUpdateEffect({
                type: e.target.value as EffectType,
                message: undefined,
                optionKey: undefined,
                label: undefined,
                allowedServiceSets: undefined,
                allowedServiceTypeKeys: undefined,
              })
            }
          >
            <MenuItem value="GUIDE_MESSAGE">{EFFECT_TYPE_LABELS.GUIDE_MESSAGE}</MenuItem>
            <MenuItem value="OPTION_HINT">{EFFECT_TYPE_LABELS.OPTION_HINT}</MenuItem>
            <MenuItem value="SERVICE_TYPE_CONSTRAINT">
              {EFFECT_TYPE_LABELS.SERVICE_TYPE_CONSTRAINT}
            </MenuItem>
          </Select>
        </FormControl>

        {effect.type === 'GUIDE_MESSAGE' && (
          <TextField
            variant="filled"
            size="small"
            label="메시지"
            multiline
            rows={2}
            value={effect.message ?? ''}
            onChange={(e) => onUpdateEffect({ message: e.target.value || undefined })}
            placeholder="예: 77인치 이상 TV는 2인 작업이 필요합니다."
            fullWidth
          />
        )}

        {effect.type === 'OPTION_HINT' && (
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <TextField
              variant="filled"
              size="small"
              label="옵션 키"
              value={effect.optionKey ?? ''}
              onChange={(e) => onUpdateEffect({ optionKey: e.target.value || undefined })}
              sx={{ minWidth: 120 }}
            />
            <TextField
              variant="filled"
              size="small"
              label="표시 라벨"
              value={effect.label ?? ''}
              onChange={(e) => onUpdateEffect({ label: e.target.value || undefined })}
              sx={{ minWidth: 120 }}
            />
          </Box>
        )}

        {effect.type === 'SERVICE_TYPE_CONSTRAINT' && (
          <>
            <Typography variant="caption" color="text.secondary">
              불가한 서비스를 체크하세요 (체크 = 이 조건에서 불가)
            </Typography>
            <FormGroup row sx={{ gap: 0.5 }}>
              {SERVICE_TYPE_VALUES.map((key) => (
                <FormControlLabel
                  key={key}
                  control={
                    <Checkbox
                      size="small"
                      checked={blockedTypeKeys.includes(key)}
                      onChange={() => handleServiceTypeToggle(key)}
                    />
                  }
                  label={`${SERVICE_TYPE_LABELS[key]} 불가`}
                />
              ))}
            </FormGroup>
            <Typography variant="caption" color="text.secondary">
              {blockedTypeKeys.length === 0
                ? '아무것도 체크하지 않으면 이 규칙은 서비스를 제한하지 않습니다.'
                : `상담에서 선택 가능: ${
                    enabledServiceSets.length > 0
                      ? enabledServiceSets.map((s) => SERVICE_SET_LABELS[s]).join(', ')
                      : '없음 (모든 서비스 범위가 막힙니다)'
                  }`}
            </Typography>
          </>
        )}
      </Box>

      <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
        <Button size="small" onClick={handleCancel}>
          취소
        </Button>
        <Button size="small" variant="contained" onClick={handleDone}>
          완료
        </Button>
      </Box>
    </Box>
  );
}
